import { categoriesWithRoom } from './capacity';
import { nextOrder } from './order';
import type { Category, Phrase } from '../types';

/**
 * Keeping the sentence that is already on screen (`AUT-05`, ADR-041).
 *
 * Everything here is decidable without React or storage, which is the point: the control itself is one
 * button in the contextual band's frame, and the two questions it turns on — *where would this go* and
 * *is the offer still standing* — are the ones worth asserting.
 *
 * ── Why the offer is derived from the buffer rather than dismissed by a handler ──
 *
 * The rule ADR-041 states is "mounted only while a spoken sentence has not been kept and nothing has been
 * typed since". A dismissal handler would mean the composer calling into authoring on every keystroke —
 * a dependency the wrong way round, and a second place where "has anything been typed" is answered. So
 * the offer records the buffer as it was when it was raised and compares: a letter, a delete, a word
 * delete, an undo and a clear all change that text, and every one of them is a reason to stop offering.
 */

/**
 * Where a kept sentence goes if that category has a slot: it came from talking. `MINED_HOME` in
 * `LearningPanel.tsx` holds the same preference for the same reason, and both are preferences rather
 * than destinations. `cat-gespraech` used to ship exactly full, so the preference never won and a kept
 * sentence always fell through; since ADR-044 raised the cap it has three slots free, so a kept sentence
 * now lands where a caregiver looks for it first.
 */
export const KEEP_HOME = 'cat-gespraech';

export interface KeepOffer {
  /** What was spoken, and what the tile will say. Never logged (`INV-06`). */
  sentence: string;
  /** Resolved when the offer was raised, so the press itself cannot fail. */
  categoryId: string;
  /** The composer's text at that moment. Anything else means something has been typed since. */
  textAtOffer: string;
}

/**
 * The category a kept sentence can land in, or `null` when the board has no room for it.
 *
 * `null` is why the offer is resolved before it is raised rather than when it is pressed: the control is
 * one flex unit wide, with nowhere to say why it refused, so a board with no room means no offer at all
 * (`AUT-08` — a phrase the person cannot see is a sentence they cannot say).
 */
export function keepDestination(
  categories: readonly Category[],
  phrases: readonly Phrase[],
): Category | null {
  const withRoom = categoriesWithRoom(categories, phrases);
  return withRoom.find((category) => category.id === KEEP_HOME) ?? withRoom[0] ?? null;
}

/** The new tile. `nowMs` rather than `Date.now()` so the id is the caller's problem, not a clock's. */
export function phraseFromKept({
  text,
  categoryId,
  phrases,
  nowMs,
}: {
  text: string;
  categoryId: string;
  phrases: readonly Phrase[];
  nowMs: number;
}): Phrase {
  return {
    id: `ph-behalten-${nowMs}`,
    categoryId,
    text,
    shortLabel: shortLabelFor(text),
    // Per category, because that is the only comparison `Phrase.order` is ever used for (`AUT-02`).
    order: nextOrder(phrases.filter((phrase) => phrase.categoryId === categoryId)),
    pinned: false,
    useCount: 0,
  };
}

/**
 * The offer, if it still applies. Same shape as `bandTenant()`: one rule, in one place, asserted.
 */
export function keepStillOffered(offer: KeepOffer | null, composerText: string): KeepOffer | null {
  if (offer === null) return null;
  return composerText === offer.textAtOffer ? offer : null;
}

/**
 * A tile label short enough to read at a glance: the first few words.
 *
 * Shared with the mining path, which had it privately first. Two rules for what a generated tile says
 * would drift, and the tile is the thing the person actually looks at.
 */
export function shortLabelFor(text: string): string {
  const words = text.trim().split(/\s+/u);
  return words.length <= 4 ? text.trim() : `${words.slice(0, 4).join(' ')}…`;
}
